import React, { useState, useEffect } from 'react';
import { callsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Phone, 
  Clock, 
  FileText, 
  Bot, 
  Play,
  Calendar,
  MapPin,
  ExternalLink,
  Loader2
} from 'lucide-react';

export default function CallsPage() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    fetchCalls();
  }, []);

  const fetchCalls = async () => {
    try {
      const response = await callsApi.getAll({ limit: 100 });
      setCalls(response.data);
    } catch (error) {
      console.error('Fetch calls error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      initiated: 'bg-blue-100 text-[#002FA7]',
      recorded: 'bg-amber-100 text-[#D97706]',
      transcribed: 'bg-green-100 text-[#16A34A]',
      completed: 'bg-gray-100 text-[#71717A]'
    };
    return styles[status] || styles.initiated;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002FA7]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calls-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Storico Chiamate
        </h1>
        <p className="text-[#71717A] mt-1">Visualizza tutte le chiamate registrate con trascrizioni e analisi IA</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-[#E4E4E7] shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Totale Chiamate</p>
                <p className="text-2xl font-medium mt-1">{calls.length}</p>
              </div>
              <Phone className="w-8 h-8 text-[#002FA7] opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-[#E4E4E7] shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Trascrizioni</p>
                <p className="text-2xl font-medium mt-1">{calls.filter(c => c.transcription).length}</p>
              </div>
              <FileText className="w-8 h-8 text-[#16A34A] opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-[#E4E4E7] shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Analisi IA</p>
                <p className="text-2xl font-medium mt-1">{calls.filter(c => c.ai_summary).length}</p>
              </div>
              <Bot className="w-8 h-8 text-[#D97706] opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calls List */}
      <Card className="border border-[#E4E4E7] shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Elenco Chiamate
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {calls.length === 0 ? (
            <div className="p-12 text-center">
              <Phone className="w-12 h-12 mx-auto mb-3 text-[#71717A] opacity-30" />
              <p className="text-[#71717A]">Nessuna chiamata registrata</p>
              <p className="text-sm text-[#71717A] mt-2">
                Le chiamate in arrivo verranno mostrate qui
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="divide-y divide-[#E4E4E7]">
                {calls.map((call) => (
                  <div 
                    key={call.id} 
                    className="p-4 hover:bg-[#F4F4F5] cursor-pointer transition-colors"
                    onClick={() => setSelectedCall(call)}
                    data-testid={`call-row-${call.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#002FA7]/10 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-[#002FA7]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#0F172A]">{call.from_number}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-[#71717A]">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(call.created_at).toLocaleDateString('it-IT')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDuration(call.duration)}
                            </span>
                          </div>
                          {call.transcription && (
                            <p className="text-sm text-[#71717A] mt-2 line-clamp-2">
                              {call.transcription.substring(0, 100)}...
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={getStatusBadge(call.status)}>
                          {call.status}
                        </Badge>
                        <div className="flex gap-1">
                          {call.transcription && (
                            <Badge variant="outline" className="text-xs">
                              <FileText className="w-3 h-3 mr-1" />
                              Trascritto
                            </Badge>
                          )}
                          {call.ai_summary && (
                            <Badge variant="outline" className="text-xs text-[#D97706] border-[#D97706]">
                              <Bot className="w-3 h-3 mr-1" />
                              IA
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Call Detail Modal */}
      <Dialog open={!!selectedCall} onOpenChange={() => setSelectedCall(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Dettagli Chiamata</DialogTitle>
          </DialogHeader>
          
          {selectedCall && (
            <div className="space-y-6">
              {/* Call Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Da</p>
                  <p className="font-medium">{selectedCall.from_number}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">A</p>
                  <p className="font-medium">{selectedCall.to_number}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Data</p>
                  <p className="font-medium">{new Date(selectedCall.created_at).toLocaleString('it-IT')}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Durata</p>
                  <p className="font-medium">{formatDuration(selectedCall.duration)}</p>
                </div>
              </div>

              {/* Recording */}
              {selectedCall.recording_url && (
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A] mb-2">Registrazione</p>
                  <Button variant="outline" asChild>
                    <a href={selectedCall.recording_url} target="_blank" rel="noopener noreferrer">
                      <Play className="w-4 h-4 mr-2" />
                      Ascolta Registrazione
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </a>
                  </Button>
                </div>
              )}

              {/* Transcription */}
              {selectedCall.transcription && (
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A] mb-2">Trascrizione</p>
                  <div className="bg-[#F4F4F5] p-4 rounded-md border border-[#E4E4E7]">
                    <p className="text-sm whitespace-pre-wrap" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {selectedCall.transcription}
                    </p>
                  </div>
                </div>
              )}

              {/* AI Summary */}
              {selectedCall.ai_summary && (
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A] mb-2 flex items-center gap-2">
                    <Bot className="w-4 h-4 text-[#D97706]" />
                    Analisi IA
                  </p>
                  <div className="bg-[#FFFBEB] p-4 rounded-md border border-[#FDE047]">
                    <p className="text-sm whitespace-pre-wrap">
                      {selectedCall.ai_summary}
                    </p>
                  </div>
                </div>
              )}

              {/* Linked Ticket */}
              {selectedCall.ticket_id && (
                <div className="pt-4 border-t border-[#E4E4E7]">
                  <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A] mb-2">Ticket Associato</p>
                  <Button variant="outline" asChild>
                    <a href={`/tickets/${selectedCall.ticket_id}`}>
                      Vai al Ticket #{selectedCall.ticket_id.slice(-8)}
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </a>
                  </Button>
                </div>
              )}

              {/* Call SID */}
              <div className="pt-4 border-t border-[#E4E4E7]">
                <p className="text-xs text-[#71717A]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Call SID: {selectedCall.call_sid}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
